import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
  message as antMessage,
} from "antd";
import {
  ExperimentOutlined,
  InboxOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";

import AIService from "../services/AIService";
import NeuroAnalysisResult, {
  OptimalNeuroImage,
  neuroScoreColor,
} from "./NeuroAnalysisResult";
import { runPool } from "../services/assignmentService";
import { usePasteFiles } from "../hooks/usePasteFiles";
import {
  DEFAULT_ANALYSIS_MODEL,
  GEMINI_VISION_MODEL,
  NEURO_MODEL_OPTIONS,
  OPENAI_LATEST_MODEL,
  normalizeModelSelection,
} from "../services/modelCatalog";

const { Dragger } = Upload;
const { Title, Text } = Typography;

/**
 * The batch ceiling. Every chat runs the full neuro pipeline — a Tribe v2 GPU
 * scan, two independent model analyses, a synthesis pass, the 10/10 prompt and
 * an image generation — so 20 is already a long-running job.
 */
const MAX_UPLOADS = 20;

/** How many chats run their pipeline at once. */
const CONCURRENCY = 3;

const STATUS_META = {
  queued: { color: "default", label: "Queued" },
  analyzing: { color: "processing", label: "Reading creative" },
  neuro: { color: "processing", label: "Neuro analysis" },
  imaging: { color: "processing", label: "Rendering 10/10" },
  done: { color: "success", label: "Ready" },
  error: { color: "error", label: "Failed" },
};

const BUSY_STATUSES = ["queued", "analyzing", "neuro", "imaging"];

const uid = () =>
  `neuro_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const isVideoFile = (file) =>
  String(file?.type || "").toLowerCase().startsWith("video");

/** One creative = one chat: image, folded report, 10/10 render, thread. */
function NeuroChat({ chat, onSend, onRetry, onRegenerateImage }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat?.messages?.length, chat?.chatBusy]);

  const busy = BUSY_STATUSES.includes(chat.status);
  const result = chat.result;

  const send = () => {
    const text = draft.trim();
    if (!text || chat.chatBusy) return;
    setDraft("");
    onSend(chat.id, text);
  };

  return (
    <Card
      className="panel-card"
      title={
        <Space size={8}>
          {chat.mediaKind === "video" ? <PlayCircleOutlined /> : <PictureOutlined />}
          <span
            style={{ textTransform: "none", letterSpacing: 0, fontSize: 14 }}
          >
            {chat.filename}
          </span>
        </Space>
      }
      extra={
        <Space size={6}>
          {result ? (
            <Tag
              style={{
                marginInlineEnd: 0,
                fontWeight: 700,
                color: neuroScoreColor(result.overallNeuroScore),
              }}
            >
              {result.overallNeuroScore}/10
            </Tag>
          ) : null}
          <Tag color={STATUS_META[chat.status]?.color} style={{ marginInlineEnd: 0 }}>
            {STATUS_META[chat.status]?.label}
          </Tag>
          {chat.status === "error" ? (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => onRetry(chat.id)}
            >
              Retry
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {/* The uploaded creative */}
        <div
          style={{
            width: "100%",
            maxHeight: 320,
            background: "rgba(148, 163, 184, 0.12)",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {chat.previewUrl ? (
            chat.mediaKind === "video" ? (
              <video
                src={chat.previewUrl}
                controls
                style={{ width: "100%", maxHeight: 320, objectFit: "contain" }}
              />
            ) : (
              <img
                src={chat.previewUrl}
                alt={chat.filename}
                style={{ width: "100%", maxHeight: 320, objectFit: "contain" }}
              />
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Preview unavailable"
            />
          )}
        </div>

        {chat.status === "error" ? (
          <Alert
            type="error"
            showIcon
            message="Neuro analysis failed"
            description={chat.error}
          />
        ) : null}

        {busy ? (
          <div
            style={{
              background: "rgba(148, 163, 184, 0.06)",
              borderRadius: 8,
              padding: "10px 14px",
            }}
          >
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              <Space size="small">
                <Spin size="small" />
                <Text strong style={{ fontSize: 13 }}>
                  {chat.stepMessage || "Waiting in the queue…"}
                </Text>
              </Space>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { key: "tribe", label: "Brain Scan" },
                  { key: "delegation", label: "GPT + Gemini" },
                  { key: "synthesis", label: "Synthesis" },
                  { key: "optimal_prompt", label: "10/10 Prompt" },
                ].map((s) => {
                  const order = [
                    "tribe",
                    "delegation",
                    "synthesis",
                    "optimal_prompt",
                  ];
                  const step = chat.step || "";
                  const stepBase = step.replace("_done", "");
                  const done =
                    step === "complete" ||
                    step === `${s.key}_done` ||
                    (order.indexOf(stepBase) > -1 &&
                      order.indexOf(s.key) < order.indexOf(stepBase));
                  const active = step === s.key || step === `${s.key}_done`;
                  return (
                    <Tag
                      key={s.key}
                      color={done ? "green" : active ? "processing" : "default"}
                      style={{ fontSize: 11 }}
                    >
                      {done ? "✓" : active ? "●" : "○"} {s.label}
                    </Tag>
                  );
                })}
              </div>
            </Space>
          </div>
        ) : null}

        {/* The analysis itself — folded away by default. */}
        {result ? (
          <Collapse
            ghost
            size="small"
            defaultActiveKey={[]}
            items={[
              {
                key: "neuro",
                label: (
                  <Space size={8}>
                    <ExperimentOutlined />
                    <Text strong>Neuro analysis</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      score {result.overallNeuroScore}/10
                    </Text>
                  </Space>
                ),
                children: (
                  <NeuroAnalysisResult
                    result={result}
                    fullResult={chat.neuro}
                    showOptimalImage={false}
                  />
                ),
              },
            ]}
          />
        ) : null}

        {/* The 10/10 render stays visible whether or not the report is open. */}
        {chat.optimalImage || chat.optimalImageLoading || chat.optimalImageError ? (
          <div
            style={{
              background: "rgba(24, 144, 255, 0.06)",
              borderRadius: 8,
              padding: "10px 14px",
              border: "1px solid rgba(24, 144, 255, 0.15)",
            }}
          >
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              <Space
                style={{ width: "100%", justifyContent: "space-between" }}
                align="start"
              >
                <Text
                  strong
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  10/10 Neuro Score Variation
                </Text>
                {chat.neuro?.optimalPrompt?.prompt ? (
                  <Button
                    size="small"
                    loading={chat.optimalImageLoading}
                    onClick={() => onRegenerateImage(chat.id)}
                  >
                    {chat.optimalImage ? "Regenerate" : "Generate"}
                  </Button>
                ) : null}
              </Space>
              <OptimalNeuroImage
                image={chat.optimalImage}
                loading={chat.optimalImageLoading}
                error={chat.optimalImageError}
                predicted={chat.neuro?.optimalPrompt?.predictedScoreImprovement}
              />
            </Space>
          </div>
        ) : null}

        {/* The chat: ask follow-ups about THIS creative only. */}
        <div
          style={{
            borderTop: "1px solid var(--app-border)",
            paddingTop: 12,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              opacity: 0.7,
              fontWeight: 600,
            }}
          >
            Chat
          </Text>

          <div
            ref={scrollRef}
            style={{ maxHeight: 260, overflowY: "auto", margin: "10px 0" }}
          >
            {chat.messages.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {result
                  ? "Ask a follow-up — “why is memory encoding so low?”, “what would push attention to a 9?”, “rewrite the 10/10 prompt for a 4:5 placement”."
                  : "The chat opens up as soon as this creative's analysis lands."}
              </Text>
            ) : (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                {chat.messages.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "88%",
                      marginLeft: m.role === "user" ? "auto" : 0,
                      padding: "9px 12px",
                      borderRadius: 10,
                      background:
                        m.role === "user"
                          ? "var(--app-surface-3)"
                          : "var(--app-surface-2)",
                      border: "1px solid var(--app-border)",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.55,
                    }}
                  >
                    {m.content}
                  </div>
                ))}
              </Space>
            )}
            {chat.chatBusy ? (
              <div style={{ marginTop: 10 }}>
                <Spin size="small" />
              </div>
            ) : null}
          </div>

          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPressEnter={send}
              placeholder="Ask about this creative…"
              disabled={chat.chatBusy || !result}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={send}
              loading={chat.chatBusy}
              disabled={!result}
            />
          </Space.Compact>
        </div>
      </Space>
    </Card>
  );
}

export default function NeuroPage({
  apiKeys,
  aiProfile,
  analysisKnowledge,
  adCopyKnowledge,
  tags = [],
  analysisModel,
  generationAspectRatio,
  workspaceId,
}) {
  const [chats, setChats] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [running, setRunning] = useState(false);
  const [neuroModel, setNeuroModel] = useState(OPENAI_LATEST_MODEL);
  const [messageApi, contextHolder] = antMessage.useMessage();
  const pasteRef = useRef(null);
  const previewUrlsRef = useRef([]);

  const resolvedAnalysisModel = normalizeModelSelection(
    analysisModel,
    DEFAULT_ANALYSIS_MODEL,
  );

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    },
    [],
  );

  const patch = useCallback((id, updates) => {
    setChats((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    );
  }, []);

  const makeService = useCallback(
    () =>
      new AIService({
        ...(apiKeys || {}),
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        generationAspectRatio,
        workspaceId,
      }),
    [
      apiKeys,
      aiProfile,
      analysisKnowledge,
      adCopyKnowledge,
      tags,
      generationAspectRatio,
      workspaceId,
    ],
  );

  const addFiles = useCallback(
    (files) => {
      const incoming = Array.from(files || []).filter(
        (f) =>
          String(f.type || "").startsWith("image") ||
          String(f.type || "").startsWith("video"),
      );
      if (!incoming.length) return;
      setFileList((prev) => {
        const room = MAX_UPLOADS - prev.length;
        if (room <= 0) {
          messageApi.warning(`You can analyze up to ${MAX_UPLOADS} uploads at a time.`);
          return prev;
        }
        if (incoming.length > room) {
          messageApi.warning(
            `Only the first ${room} added — the batch caps at ${MAX_UPLOADS} uploads.`,
          );
        }
        return [...prev, ...incoming.slice(0, room)];
      });
    },
    [messageApi],
  );

  usePasteFiles({
    ref: pasteRef,
    onFiles: addFiles,
    allowedTypes: ["image/", "video/"],
  });

  /** Render the 10/10 prompt into an image. Split out so it can be re-run. */
  const renderOptimalImage = useCallback(
    async (id, prompt) => {
      if (!prompt) return;
      patch(id, { optimalImageLoading: true, optimalImageError: "" });
      try {
        const imageUrl = await makeService().generateImageFromPrompt({
          prompt,
          aspectRatio: generationAspectRatio || "1:1",
        });
        patch(id, { optimalImage: imageUrl, optimalImageLoading: false });
      } catch (error) {
        patch(id, {
          optimalImageLoading: false,
          optimalImageError: error?.message || "Image generation failed.",
        });
      }
    },
    [generationAspectRatio, makeService, patch],
  );

  /**
   * The per-creative pipeline — the same one the Analysis panel's Neuro tab
   * runs, just once per upload instead of once per session.
   */
  const processChat = useCallback(
    async (chat) => {
      const { id, file } = chat;
      const isVideo = chat.mediaKind === "video";
      const aiService = makeService();

      try {
        patch(id, {
          status: "analyzing",
          error: "",
          stepMessage: isVideo ? "Reading the video…" : "Reading the creative…",
        });

        const rawAnalysis = await aiService.analyzeCreative(
          file,
          isVideo,
          undefined,
          undefined,
          { model: isVideo ? GEMINI_VISION_MODEL : resolvedAnalysisModel },
        );
        const masterContext = isVideo
          ? rawAnalysis?.segments
            ? rawAnalysis
            : rawAnalysis?.combined_context || rawAnalysis
          : rawAnalysis;

        // Hand the Tribe v2 scan the actual pixels. Without bytes it falls back
        // to a text description, which is a far weaker brain-scan input.
        const neuroContext = { ...masterContext };
        if (!isVideo) {
          try {
            const prepared = await aiService.prepareOpenAIImage(file, {
              maxDim: 1024,
              quality: 0.85,
            });
            if (prepared?.base64) {
              neuroContext.imageBase64 = prepared.base64;
              neuroContext.imageMime = prepared.mimeType || "image/jpeg";
            }
          } catch (error) {
            // Non-fatal: the scan falls back to the text description.
          }
        }

        patch(id, {
          status: "neuro",
          analysis: rawAnalysis,
          masterContext,
          stepMessage: "Running Tribe v2 brain scan…",
        });

        const fullResult = await aiService.runFullNeuroAnalysis(neuroContext, {
          mediaType: isVideo ? "video" : "image",
          model: neuroModel,
          onProgress: ({ step, message: msg }) => {
            patch(id, { step, stepMessage: msg || "" });
          },
        });

        patch(id, {
          status: fullResult?.optimalPrompt?.prompt ? "imaging" : "done",
          neuro: fullResult,
          result: fullResult.synthesized,
          step: "complete",
          stepMessage: "",
        });

        if (fullResult?.optimalPrompt?.prompt) {
          await renderOptimalImage(id, fullResult.optimalPrompt.prompt);
          patch(id, { status: "done" });
        }
      } catch (error) {
        patch(id, {
          status: "error",
          stepMessage: "",
          error: error?.payload?.error || error?.message || "Something went wrong.",
        });
      }
    },
    [makeService, neuroModel, patch, renderOptimalImage, resolvedAnalysisModel],
  );

  const run = useCallback(async () => {
    if (!fileList.length) return;

    const fresh = fileList.slice(0, MAX_UPLOADS).map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);
      return {
        id: uid(),
        file,
        filename: file.name,
        mediaKind: isVideoFile(file) ? "video" : "image",
        previewUrl,
        status: "queued",
        error: "",
        step: "",
        stepMessage: "",
        analysis: null,
        masterContext: null,
        neuro: null,
        result: null,
        optimalImage: null,
        optimalImageLoading: false,
        optimalImageError: "",
        messages: [],
        chatBusy: false,
      };
    });

    setChats((prev) => [...fresh, ...prev]);
    setFileList([]);
    setRunning(true);

    await runPool(
      fresh.map((c) => () => processChat(c)),
      { concurrency: CONCURRENCY, prime: false },
    );

    setRunning(false);
  }, [fileList, processChat]);

  const retry = useCallback(
    (id) => {
      const chat = chats.find((c) => c.id === id);
      if (!chat) return;
      patch(id, {
        status: "queued",
        error: "",
        step: "",
        stepMessage: "",
        neuro: null,
        result: null,
        optimalImage: null,
        optimalImageError: "",
      });
      processChat(chat);
    },
    [chats, patch, processChat],
  );

  const regenerateImage = useCallback(
    (id) => {
      const chat = chats.find((c) => c.id === id);
      const prompt = chat?.neuro?.optimalPrompt?.prompt;
      if (!prompt) return;
      renderOptimalImage(id, prompt);
    },
    [chats, renderOptimalImage],
  );

  const send = useCallback(
    async (id, text) => {
      const chat = chats.find((c) => c.id === id);
      if (!chat?.result) return;

      const history = [...chat.messages, { role: "user", content: text }];
      patch(id, { messages: history, chatBusy: true });

      try {
        const reply = await makeService().chatAboutNeuroAnalysis({
          synthesized: chat.result,
          optimalPrompt: chat.neuro?.optimalPrompt || null,
          tribeResult: chat.neuro?.tribeResult || null,
          context: chat.masterContext,
          fileName: chat.filename,
          isVideo: chat.mediaKind === "video",
          history: chat.messages,
          message: text,
          model: neuroModel,
        });
        patch(id, {
          messages: [
            ...history,
            { role: "assistant", content: reply || "No answer returned." },
          ],
          chatBusy: false,
        });
      } catch (error) {
        patch(id, {
          messages: [
            ...history,
            {
              role: "assistant",
              content: `⚠️ ${error?.payload?.error || error?.message || "Request failed."}`,
            },
          ],
          chatBusy: false,
        });
      }
    },
    [chats, makeService, neuroModel, patch],
  );

  const { done, failed, pct } = useMemo(() => {
    const doneCount = chats.filter((c) => c.status === "done").length;
    const failedCount = chats.filter((c) => c.status === "error").length;
    return {
      done: doneCount,
      failed: failedCount,
      pct: chats.length
        ? Math.round(((doneCount + failedCount) / chats.length) * 100)
        : 0,
    };
  }, [chats]);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {contextHolder}

      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Neuro
        </Title>
        <Text type="secondary">
          Drop up to {MAX_UPLOADS} creatives. Each one gets the full Meta Tribe v2
          brain scan, a neuroscience-backed analysis, a 10/10 variation, and its
          own chat.
        </Text>
      </div>

      <Card className="panel-card" title="Upload">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <div ref={pasteRef} tabIndex={0} className="paste-upload-area">
              <Dragger
                multiple
                accept="image/*,video/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  addFiles([file]);
                  return false;
                }}
                fileList={[]}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Drop creatives here, or paste</p>
                <p className="ant-upload-hint">
                  Images and videos — up to {MAX_UPLOADS} per batch.
                </p>
              </Dragger>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div>
                <Text style={{ fontSize: 12 }}>Neuro model</Text>
                <Select
                  size="small"
                  style={{ width: "100%", marginTop: 4 }}
                  value={neuroModel}
                  onChange={setNeuroModel}
                  options={NEURO_MODEL_OPTIONS}
                  disabled={running}
                />
              </div>

              {fileList.length ? (
                <div className="post-upload-pills">
                  {fileList.slice(0, 8).map((f, i) => (
                    <span key={i} className="post-upload-pill">
                      {isVideoFile(f) ? <PlayCircleOutlined /> : <PictureOutlined />}{" "}
                      {f.name}
                    </span>
                  ))}
                  {fileList.length > 8 ? (
                    <span className="post-upload-pill post-upload-pill--more">
                      +{fileList.length - 8}
                    </span>
                  ) : null}
                </div>
              ) : null}

              <Space>
                <Button
                  type="primary"
                  icon={<ExperimentOutlined />}
                  loading={running}
                  onClick={run}
                  disabled={!fileList.length}
                >
                  Run neuro analysis
                  {fileList.length ? ` (${fileList.length})` : ""}
                </Button>
                <Button
                  onClick={() => setFileList([])}
                  disabled={running || !fileList.length}
                >
                  Clear
                </Button>
              </Space>

              {running ? (
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Progress percent={pct} size="small" />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {done} done{failed ? `, ${failed} failed` : ""} of{" "}
                    {chats.length}
                  </Text>
                </Space>
              ) : null}
            </Space>
          </Col>
        </Row>
      </Card>

      {chats.length ? (
        <Row gutter={[16, 16]} align="top">
          {chats.map((chat) => (
            <Col key={chat.id} xs={24} xl={12}>
              <NeuroChat
                chat={chat}
                onSend={send}
                onRetry={retry}
                onRegenerateImage={regenerateImage}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="panel-card">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No creatives analyzed yet. Drop a batch above to start."
          />
        </Card>
      )}
    </Space>
  );
}
